// Emergent Google Auth — frontend client (iter-35).
//
// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS,
// THIS BREAKS THE AUTH. All redirect logic derives from window.location.origin.
//
// Flow:
//   1. `beginLogin()` — sends the user to https://auth.emergentagent.com
//      with a redirect back to /sessions on THIS origin.
//   2. Google auth completes → user lands at `{origin}/sessions#session_id=...`.
//   3. `AuthCallback` reads the hash, POSTs to /api/auth/session, and the
//      backend sets the httpOnly session_token cookie.
//   4. Subsequent `Auth.me()` calls send `credentials: 'include'` so the
//      cookie flows across origins.
import axios from "axios";
import { getDeviceId } from "./device";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const AUTH_API = `${BACKEND_URL}/api/auth`;

// Axios instance that carries cookies across our cross-origin PWA <-> API.
export const authApi = axios.create({
  baseURL: AUTH_API,
  withCredentials: true,
  headers: { "Content-Type": "application/json", "X-Device-Id": getDeviceId() },
});

const POST_LOGIN_KEY = "ssc:postLogin";

export const Auth = {
  beginLogin(next) {
    if (next) sessionStorage.setItem(POST_LOGIN_KEY, next);
    // Land back at /sessions — the app's true "main" surface post-login.
    const redirectUrl = window.location.origin + "/sessions";
    window.location.href =
      "https://auth.emergentagent.com/?redirect=" + encodeURIComponent(redirectUrl);
  },
  consumePostLogin() {
    const next = sessionStorage.getItem(POST_LOGIN_KEY);
    sessionStorage.removeItem(POST_LOGIN_KEY);
    return next && next.startsWith("/") && !next.startsWith("//") ? next : "/sessions";
  },
  exchangeSession(sessionId) {
    return authApi.post("/session", { session_id: sessionId }).then((r) => r.data);
  },
  me() {
    return authApi.get("/me").then((r) => r.data);
  },
  logout() {
    return authApi.post("/logout").then((r) => r.data);
  },
};

// ============ Admin: Audit + Health (iter-34) ============
// Same base as /api Admin.* helpers (import them from lib/api.js) — reused
// here because auth pages live outside the Admin cockpit context.
const ADMIN_API = `${BACKEND_URL}/api/v1/admin`;
const HEALTH_API = `${BACKEND_URL}/api/health`;

const adminHeaders = () => {
  const t =
    typeof localStorage !== "undefined"
      ? localStorage.getItem("ssc:adminToken")
      : null;
  return t ? { "X-Admin-Token": t } : {};
};

export const AdminAudit = {
  list: (opts = {}) => {
    const params = new URLSearchParams();
    if (opts.limit) params.set("limit", String(opts.limit));
    if (opts.action_prefix) params.set("action_prefix", opts.action_prefix);
    if (opts.actor_kind) params.set("actor_kind", opts.actor_kind);
    if (opts.outcome) params.set("outcome", opts.outcome);
    return axios
      .get(`${ADMIN_API}/audit-log?${params.toString()}`, { headers: adminHeaders() })
      .then((r) => r.data);
  },
};

export const HealthDeep = {
  fetch: () => axios.get(`${HEALTH_API}/deep`).then((r) => r.data),
};
